// Heatmap dia × hora — grid CSS puro. Cor da célula via color-mix do token
// var(--brand-green). Sem libs externas (perf §13). A11y: cada célula tem
// aria-label descritivo + role="img".
//
// A faixa de horas é parametrizável (`hours`): o default 08–20h serve o volume
// comercial de /empresas, e MET-06 ("conversas fora do horário") precisa das 24.
// ⚠️ Com 24 colunas o grid passa de ~400px — envolva em <TableScroll> nas telas
// estreitas, senão a página inteira rola de lado.
import { useMemo } from 'react';
import styles from './Heatmap.module.css';

export interface HeatmapCell {
  /** 0=domingo, 6=sábado. */
  dayOfWeek: number;
  /** 0..23, hora local. */
  hour: number;
  count: number;
}

const DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

/** Janela comercial 08h–20h (13 horas) — o recorte histórico de /empresas. */
export const BUSINESS_HOURS: readonly number[] = Array.from({ length: 13 }, (_, i) => 8 + i);

/** Dia inteiro (MET-06). */
export const FULL_DAY_HOURS: readonly number[] = Array.from({ length: 24 }, (_, i) => i);

export interface HeatmapProps {
  cells: HeatmapCell[];
  /** Texto pro <caption>/aria-label do grid. */
  caption?: string;
  /** Exibe a contagem dentro de cada célula (detalhe da loja). Default: false. */
  showValues?: boolean;
  /** Horas exibidas nas colunas. Default: 08h–20h. */
  hours?: readonly number[];
  /** Substantivo plural do que a célula conta ("conversas", "atendimentos"). */
  unitLabel?: string;
}

export function Heatmap({
  cells,
  caption = 'Volume de conversas por dia da semana e hora',
  showValues = false,
  hours = BUSINESS_HOURS,
  unitLabel = 'conversas',
}: HeatmapProps) {
  const map = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of cells) m.set(`${c.dayOfWeek}:${c.hour}`, c.count);
    return m;
  }, [cells]);

  const max = useMemo(() => Math.max(1, ...cells.map((c) => c.count)), [cells]);

  return (
    <figure className={styles.figure} role="group" aria-label={caption}>
      <div
        className={styles.grid}
        data-values={showValues || undefined}
        style={{ gridTemplateColumns: `auto repeat(${hours.length}, 1fr)` }}
      >
        <div className={styles.corner} aria-hidden="true" />
        {hours.map((h) => (
          <div key={h} className={styles.hourLabel}>
            {String(h).padStart(2, '0')}h
          </div>
        ))}
        {DAYS.map((day, di) => (
          <Row
            key={day}
            di={di}
            dayLabel={day}
            max={max}
            map={map}
            showValues={showValues}
            hours={hours}
            unitLabel={unitLabel}
          />
        ))}
      </div>
      <figcaption className={styles.legend}>
        <span>Menos</span>
        {[0.1, 0.3, 0.55, 0.85].map((opacity) => (
          <span
            key={opacity}
            className={styles.legendSwatch}
            style={{
              background: `color-mix(in oklab, var(--brand-green) ${Math.round(opacity * 100)}%, var(--color-surface))`,
            }}
          />
        ))}
        <span>Mais</span>
      </figcaption>
    </figure>
  );
}

interface RowProps {
  di: number;
  dayLabel: string;
  max: number;
  map: Map<string, number>;
  showValues: boolean;
  hours: readonly number[];
  unitLabel: string;
}

function Row({ di, dayLabel, max, map, showValues, hours, unitLabel }: RowProps) {
  return (
    <>
      <div className={styles.dayLabel}>{dayLabel}</div>
      {hours.map((h) => {
        const v = map.get(`${di}:${h}`) ?? 0;
        const intensity = max > 0 ? v / max : 0;
        const bg = `color-mix(in oklab, var(--brand-green) ${Math.round(intensity * 80)}%, var(--color-surface))`;
        return (
          <div
            key={`${di}-${h}`}
            role="img"
            aria-label={`${dayLabel} ${h}h: ${v} ${unitLabel}`}
            title={`${dayLabel} ${h}h: ${v} ${unitLabel}`}
            className={styles.cell}
            style={{ background: bg }}
            data-day={di}
            data-hour={h}
            data-count={v}
          >
            {showValues ? v : null}
          </div>
        );
      })}
    </>
  );
}
